// SINGLE SOURCE OF TRUTH for every public claim Creatara makes.
// Values here MUST match what the backend actually enforces:
//   backend/usage_guard.py (TEXT/IMAGE/VIDEO_QUOTAS), routes/payments.py (SUBSCRIPTION_PLANS),
//   routes/social.py (REFERRAL_CREDITS_PER_SIGNUP), server.py (reset_monthly_quotas).
// Never hardcode these numbers in the UI — import from here.

export const CLAIMS = {
  brand: "Creatara",

  company: {
    legalName: "ProJohnson Consulting Ltd",
    registration: "Registered in England & Wales",
    country: "United Kingdom",
    supportEmail: "support@creatara.ai",
    supportResponse: "Email support — we aim to reply within 2 business days.",
  },

  // ---- Free allowance (authoritative) ----
  freeAllowance: {
    generations: 10,
    period: "month",
    resetRule: "Resets on the 1st of every month (00:05 UTC)",
  },

  generationDefinition:
    "One generation = one AI text output (a post, thread, caption, script or blog draft) produced from a single prompt. AI images and video clips have their own separate monthly allowances and don't consume text generations.",

  creditsRunOut:
    "When you reach your monthly cap, text generation pauses until the next monthly reset. Everything else keeps working — your saved content, scheduled posts, tracked links, lead forms and analytics. Upgrading, or earning referral credits, lifts the cap immediately.",

  referral: {
    creditsPerSignup: 5,
    mechanics:
      "Every friend who signs up with your referral link permanently adds 5 generations to your monthly cap. Credits stack and never expire.",
  },

  // ---- Plans (exact entitlements, matching backend enforcement) ----
  plans: [
    {
      id: "free",
      name: "Free",
      price: "£0",
      period: "/month",
      highlight: false,
      entitlements: [
        "10 AI text generations / month (+5 per referral, permanent)",
        "2 AI images / month",
        "Scheduling + auto-publish on supported networks",
        "Affiliate link tracking & lead capture forms",
        "Content calendar & templates",
      ],
      detail: { text: "10 / month", images: "2 / month", videos: "Not included" },
    },
    {
      id: "pro",
      name: "Pro",
      price: "£29",
      period: "/month",
      highlight: true,
      entitlements: [
        "100 AI text generations / month",
        "30 AI images / month",
        "5 AI video clips (Sora 2) / month",
        "5 Revenue Campaigns / month",
        "Everything in Free",
        "Priority support",
      ],
      detail: { text: "100 / month", images: "30 / month", videos: "5 / month" },
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "£99",
      period: "/month",
      highlight: false,
      entitlements: [
        "Unlimited AI text generations",
        "200 AI images / month",
        "50 AI video clips / month",
        "30 Revenue Campaigns / month",
        "API access & white-label options",
        "Dedicated support",
      ],
      detail: { text: "Unlimited", images: "200 / month", videos: "50 / month" },
    },
  ],

  // ---- Billing transparency ----
  billing: {
    currency: "All prices in GBP (£).",
    tax: "Any applicable VAT / sales tax is calculated and shown at checkout by Stripe.",
    cycle: "Billed monthly. Plans renew automatically each month until cancelled.",
    cancel:
      "Cancel anytime from Settings → Subscription → Manage billing (Stripe customer portal). Your plan stays active until the end of the paid period.",
    refunds: "7-day money-back guarantee on your first payment — email support@creatara.ai.",
    afterCancellation:
      "After cancellation you move to the Free plan. All your content, tracked links, lead forms and analytics are retained; scheduled posts remain scheduled. Only generation allowances change. You can permanently delete your account and all data anytime from Settings → Danger Zone.",
  },

  // ---- Honest positioning copy ----
  hero: {
    kicker: "Built for solopreneurs who need customers, not more content",
    headline: "Turn one idea into a week of content that generates leads",
    subline:
      "Describe what you're promoting. Creatara drafts a coordinated campaign for your review, schedules it to your connected channels, and tracks the clicks and leads it produces — one workflow, start to finish.",
    cta: "Create My First Campaign",
  },

  publishSentence:
    "Auto-publishes to X, LinkedIn, Bluesky and Telegram once you connect your accounts. Facebook & Instagram are one-click manual share today — auto-publish to your own pages is pending Meta App Review.",

  // ---- Integration matrix (only mark autoPublish where the app genuinely
  //      posts to the USER'S OWN account without human action today) ----
  integrations: [
    {
      network: "X (Twitter)",
      actions: "Draft · Schedule · Auto-publish",
      autoPublish: true,
      plan: "All plans",
      requirements:
        "Connect your X account in Settings (OAuth 1.0a — read & write posting permission). If not connected, scheduled posts go out via the shared Creatara account instead.",
      limitations:
        "280-character limit (longer posts are auto-truncated) · one video clip per post supported · X API rate limits apply.",
    },
    {
      network: "LinkedIn",
      actions: "Draft · Schedule · Auto-publish",
      autoPublish: true,
      plan: "All plans",
      requirements:
        "Connect your LinkedIn account in Settings (OAuth 2.0 — scopes: openid, profile, w_member_social).",
      limitations:
        "Text + one image · posts to your personal profile (not company pages) · access tokens expire after ~60 days — we email you before it lapses and show a reconnect prompt.",
    },
    {
      network: "Bluesky",
      actions: "Draft · Schedule · Auto-publish",
      autoPublish: true,
      plan: "All plans",
      requirements: "Your Bluesky handle + an app password (created in Bluesky settings — no OAuth).",
      limitations: "300-character limit · text-only · cross-posts automatically alongside any scheduled post.",
    },
    {
      network: "Telegram",
      actions: "Draft · Schedule · Auto-publish",
      autoPublish: true,
      plan: "All plans",
      requirements: "Your own bot token (via @BotFather) + a channel or group where the bot is admin.",
      limitations: "Posts text + image to your configured chat only · cross-posts automatically alongside any scheduled post.",
    },
    {
      network: "Facebook",
      actions: "Draft · One-click share",
      autoPublish: false,
      pending: true,
      plan: "All plans",
      requirements: "None today — the Facebook share dialog opens pre-filled with your post.",
      limitations:
        "Auto-publish to your own Page is pending Meta App Review and is not available yet. Scheduled Facebook posts currently go out via Creatara's shared Page, not your own.",
    },
    {
      network: "Instagram",
      actions: "Draft · Copy-for-Instagram export",
      autoPublish: false,
      pending: true,
      plan: "All plans",
      requirements: "None today — captions are formatted and copied for manual pasting.",
      limitations:
        "Feed posts require an image · auto-publish to your own account is pending Meta App Review and is not available yet. Scheduled Instagram posts currently go via Creatara's shared account.",
    },
    {
      network: "WhatsApp",
      actions: "Draft · One-click share",
      autoPublish: false,
      plan: "All plans",
      requirements: "None — opens the WhatsApp share sheet with your text.",
      limitations: "Manual share only · optimizer targets ~700 characters. No scheduling or auto-publish.",
    },
    {
      network: "TikTok",
      actions: "Draft / script only",
      autoPublish: false,
      plan: "All plans (video clips: Pro+)",
      requirements: "None.",
      limitations:
        "No publishing or scheduling — Creatara generates scripts (and Sora 2 video clips on Pro+) that you upload to TikTok yourself.",
    },
  ],

  defaultDisclosure: "Disclosure: this post contains affiliate links — I may earn a commission if you buy through them.",
};

export const freeGenerationsLabel = () =>
  `${CLAIMS.freeAllowance.generations} free generations / ${CLAIMS.freeAllowance.period}`;
